import api from "./api";
import { STORAGE_KEYS } from "./storageKeys";
import { taskerBaseUrl } from "./taskerConfig";

const TOKEN_KEY = STORAGE_KEYS.VENDOR_TOKEN;
const MOBILE_KEY = STORAGE_KEYS.VENDOR_MOBILE;
const MOBILE_COUNTRY_CODE_KEY = STORAGE_KEYS.VENDOR_MOBILE_COUNTRY_CODE;
const REVIEWER_TOKEN_KEY = STORAGE_KEYS.REVIEWER_TOKEN;
const REVIEWER_TOKEN_EXPIRES_KEY = STORAGE_KEYS.REVIEWER_TOKEN_EXPIRES;
const REFRESH_PATH = "/auth/refresh";
const VENDOR_CFG = {
  useVendorToken: true,
  baseURL: taskerBaseUrl(),
} as const;
// _skipVendorRefresh avoids interceptor→refresh→interceptor deadlock.
const VENDOR_REFRESH_CFG = {
  useVendorToken: true,
  baseURL: taskerBaseUrl(),
  _skipVendorRefresh: true,
  skipVendor401Redirect: true,
} as const;
const REVIEWER_CFG = {
  useReviewerToken: true,
  baseURL: taskerBaseUrl(),
} as const;

const REVIEWER_TOKEN_LEEWAY_MS = 60_000;
const VENDOR_TOKEN_LEEWAY_MS = 60_000;

export type OtpSendResponse = {
  request_id?: string;
  message?: string;
  [key: string]: unknown;
};

export type OtpVerifyResponse = {
  access_token: string;
  expires_in?: number;
  [key: string]: unknown;
};

async function sendOtp(
  mobile: string,
  mobile_country_code: string = "+91",
): Promise<OtpSendResponse> {
  const data = (await api.post(
    "/auth/otp/send",
    { mobile, mobile_country_code },
    VENDOR_CFG,
  )) as OtpSendResponse;
  return data;
}

async function verifyOtp(
  request_id: string,
  otp: string,
  mobile: string,
  mobile_country_code: string = "+91",
): Promise<OtpVerifyResponse> {
  const data = (await api.post(
    "/auth/otp/verify",
    { request_id, otp, mobile_country_code },
    VENDOR_CFG,
  )) as OtpVerifyResponse;
  if (data?.access_token) {
    sessionStorage.setItem(TOKEN_KEY, data.access_token);
    sessionStorage.setItem(MOBILE_KEY, mobile);
    sessionStorage.setItem(MOBILE_COUNTRY_CODE_KEY, mobile_country_code);
  }
  return data;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function jwtExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== "number") return null;
  return exp * 1000;
}

function getTaskerToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

function getVendorMobile(): string | null {
  return sessionStorage.getItem(MOBILE_KEY);
}

function getVendorMobileCountryCode(): string | null {
  return sessionStorage.getItem(MOBILE_COUNTRY_CODE_KEY);
}

function clearTaskerSession(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(MOBILE_KEY);
  sessionStorage.removeItem(MOBILE_COUNTRY_CODE_KEY);
}

export type ReviewerExchangeResponse = {
  access_token: string;
  expires_in: number;
  [key: string]: unknown;
};

function getReviewerToken(): string | null {
  const token = sessionStorage.getItem(REVIEWER_TOKEN_KEY);
  const expiresAt = Number(
    sessionStorage.getItem(REVIEWER_TOKEN_EXPIRES_KEY) ?? "0",
  );
  if (!token || !expiresAt) return null;
  if (Date.now() >= expiresAt - REVIEWER_TOKEN_LEEWAY_MS) return null;
  return token;
}

function clearReviewerToken(): void {
  sessionStorage.removeItem(REVIEWER_TOKEN_KEY);
  sessionStorage.removeItem(REVIEWER_TOKEN_EXPIRES_KEY);
}

function getReviewerUserId(): string | null {
  const token = getReviewerToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  const sub = payload?.sub;
  return typeof sub === "string" && sub.length > 0 ? sub : null;
}

let inFlightExchange: Promise<string> | null = null;
let inFlightVendorRefresh: Promise<string | null> | null = null;
let inFlightReviewerRefresh: Promise<string | null> | null = null;
const EXCHANGE_FAILURE_COOLDOWN_MS = 5_000;
let lastFailedExchangeAt = 0;

async function refreshVendorToken(): Promise<string | null> {
  if (inFlightVendorRefresh) return inFlightVendorRefresh;
  const current = getTaskerToken();
  if (!current) return null;
  inFlightVendorRefresh = (async () => {
    try {
      const response = (await api.post(
        REFRESH_PATH,
        {},
        VENDOR_REFRESH_CFG,
      )) as { access_token?: string; expires_in?: number } | null;
      if (!response?.access_token) return null;
      sessionStorage.setItem(TOKEN_KEY, response.access_token);
      return response.access_token;
    } catch {
      return null;
    } finally {
      inFlightVendorRefresh = null;
    }
  })();
  return inFlightVendorRefresh;
}

async function ensureVendorToken(): Promise<string | null> {
  const token = getTaskerToken();
  if (!token) return null;
  const expMs = jwtExpiryMs(token);
  // Undecodable JWT payload → trust the stored token and let the 401
  // reactive path handle a genuine rejection. Otherwise every request
  // would trigger a refresh, and a broken refresh loop would hammer the
  // network with no observable improvement.
  if (expMs == null) return token;
  if (Date.now() < expMs - VENDOR_TOKEN_LEEWAY_MS) return token;
  return refreshVendorToken();
}

async function refreshReviewerToken(): Promise<string | null> {
  if (inFlightReviewerRefresh) return inFlightReviewerRefresh;
  const current = sessionStorage.getItem(REVIEWER_TOKEN_KEY);
  if (!current) return null;
  inFlightReviewerRefresh = (async () => {
    try {
      const response = (await api.post(
        REFRESH_PATH,
        {},
        REVIEWER_CFG,
      )) as { access_token?: string; expires_in?: number } | null;
      if (!response?.access_token) return null;
      const expiresAt =
        Date.now() + Math.max(1, response.expires_in || 900) * 1000;
      sessionStorage.setItem(REVIEWER_TOKEN_KEY, response.access_token);
      sessionStorage.setItem(REVIEWER_TOKEN_EXPIRES_KEY, String(expiresAt));
      return response.access_token;
    } catch {
      return null;
    } finally {
      inFlightReviewerRefresh = null;
    }
  })();
  return inFlightReviewerRefresh;
}

async function exchangeReviewerToken(): Promise<string> {
  if (inFlightExchange) return inFlightExchange;
  if (Date.now() - lastFailedExchangeAt < EXCHANGE_FAILURE_COOLDOWN_MS) {
    throw new Error(
      "Reviewer token exchange is in cooldown after recent failure",
    );
  }
  inFlightExchange = (async () => {
    try {
      const response = (await api.post(
        "/auth/exchange",
        {},
        { baseURL: taskerBaseUrl() },
      )) as ReviewerExchangeResponse;
      if (!response?.access_token) {
        throw new Error("Reviewer exchange returned no access_token");
      }
      const expiresAt =
        Date.now() + Math.max(1, response.expires_in || 900) * 1000;
      sessionStorage.setItem(REVIEWER_TOKEN_KEY, response.access_token);
      sessionStorage.setItem(REVIEWER_TOKEN_EXPIRES_KEY, String(expiresAt));
      return response.access_token;
    } catch (err) {
      lastFailedExchangeAt = Date.now();
      throw err;
    } finally {
      inFlightExchange = null;
    }
  })();
  return inFlightExchange;
}

async function ensureReviewerToken(): Promise<string> {
  const cached = getReviewerToken();
  if (cached) return cached;
  // Stale but present token — try refresh before a full re-exchange.
  const stale = sessionStorage.getItem(REVIEWER_TOKEN_KEY);
  if (stale) {
    const refreshed = await refreshReviewerToken();
    if (refreshed) return refreshed;
  }
  return exchangeReviewerToken();
}

const taskerAuth = {
  sendOtp,
  verifyOtp,
  getTaskerToken,
  getVendorMobile,
  getVendorMobileCountryCode,
  clearTaskerSession,
  getReviewerToken,
  getReviewerUserId,
  clearReviewerToken,
  ensureReviewerToken,
  ensureVendorToken,
  refreshVendorToken,
  refreshReviewerToken,
};

export default taskerAuth;
export {
  ensureReviewerToken,
  getReviewerToken,
  getReviewerUserId,
  clearReviewerToken,
  ensureVendorToken,
  refreshVendorToken,
  refreshReviewerToken,
};
